// Story moment detection for triggering sound effects
export interface StoryMoment {
  type: 'magic' | 'adventure' | 'happy' | 'funny' | 'sleepy';
  wordIndex: number;
  keyword: string;
}

// Keywords that trigger different sound effects
const momentKeywords = {
  magic: [
    'magic', 'magical', 'spell', 'wizard', 'fairy', 'enchanted', 'sparkle', 
    'shimmer', 'glitter', 'wand', 'potion', 'mystical', 'sorcerer', 'enchantment',
    'twinkle', 'glow', 'mystic', 'charm', 'hex', 'conjure'
  ],
  adventure: [
    'adventure', 'journey', 'explore', 'discover', 'quest', 'brave', 'hero',
    'flying', 'soaring', 'rushing', 'racing', 'dash', 'zoom', 'whoosh',
    'climb', 'leap', 'jump', 'run', 'chase', 'escape'
  ],
  happy: [
    'happy', 'joy', 'laugh', 'smile', 'cheerful', 'delight', 'wonderful',
    'amazing', 'fantastic', 'hooray', 'celebrate', 'party', 'dance', 'sing',
    'excited', 'thrilled', 'glad', 'merry', 'jolly', 'bliss'
  ],
  funny: [
    'funny', 'silly', 'giggle', 'hilarious', 'ridiculous', 'goofy', 'bounce',
    'boing', 'whoops', 'oops', 'bonk', 'tumble', 'wobble', 'tickle',
    'sneeze', 'hiccup', 'burp', 'squish', 'splat', 'plop'
  ],
  sleepy: [
    'sleep', 'sleepy', 'dream', 'yawn', 'rest', 'peaceful', 'calm', 'quiet',
    'gentle', 'soft', 'cozy', 'snuggle', 'blanket', 'pillow', 'lullaby',
    'drowsy', 'drift', 'float', 'serene', 'tranquil'
  ]
};

// Detect story moments in the text
export function detectStoryMoments(text: string): StoryMoment[] {
  const words = text.toLowerCase().split(/\s+/);
  const moments: StoryMoment[] = [];
  const detectedPositions = new Set<number>(); // Avoid duplicate detections

  words.forEach((word, index) => {
    // Clean word of punctuation for matching
    const cleanWord = word.replace(/[.,!?;:'"]/g, '');
    
    // Check each moment type
    for (const [momentType, keywords] of Object.entries(momentKeywords)) {
      if (keywords.includes(cleanWord) && !detectedPositions.has(index)) {
        moments.push({
          type: momentType as StoryMoment['type'],
          wordIndex: index,
          keyword: cleanWord
        });
        detectedPositions.add(index);
        break; // Only one moment type per word
      }
    }
  });

  return moments;
}

// Get the next moment after a given word index
export function getNextMoment(moments: StoryMoment[], currentWordIndex: number): StoryMoment | null {
  return moments.find(moment => moment.wordIndex > currentWordIndex) || null;
}

// Check if we should trigger a sound effect for the current word
export function shouldTriggerSound(
  moments: StoryMoment[], 
  currentWordIndex: number,
  lastTriggeredIndex: number
): StoryMoment | null {
  // Don't trigger sounds too close together (at least 5 words apart)
  if (currentWordIndex - lastTriggeredIndex < 5) {
    return null;
  }

  // Find if current word matches a moment
  const currentMoment = moments.find(moment => moment.wordIndex === currentWordIndex);
  return currentMoment || null;
}

// Get a preview of detected moments for debugging/display
export function getMomentsSummary(moments: StoryMoment[]): Record<string, number> {
  const summary: Record<string, number> = {
    magic: 0,
    adventure: 0,
    happy: 0,
    funny: 0,
    sleepy: 0
  };

  moments.forEach(moment => {
    summary[moment.type]++;
  });

  return summary;
}